import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { watchOrReadListController } from '../../controllers/watchorreadlist.controller'
import { fetchAnimesByIds } from './animeDB.slice'

const initialState = {
  watchlist: {},
  readlist: {},
  statusMap: {},
}

/* Thunks */

export const loadList = createAsyncThunk(
  'watchlist/loadList',
  async (
    { userId, listType, options = {}, cacheKey = 'default' },
    { dispatch }
  ) => {
    const { page = 1, limit = 150, ...filterBy } = options
    const list = await watchOrReadListController.query(userId, listType, {
      page,
      limit,
      ...filterBy,
    })
    if (listType === 'watchlist' && list.length) {
      await dispatch(fetchAnimesByIds(list.map((i) => i.anizone_id)))
    }
    return { userId, listType, cacheKey, page, list }
  }
)

export const loadStatusMap = createAsyncThunk(
  'watchlist/loadStatusMap',
  async ({ userId, listType, anizoneIds }) => {
    const map = await watchOrReadListController.statusMap(
      userId,
      listType,
      anizoneIds
    )
    return { userId, map }
  }
)

export const addItem = createAsyncThunk(
  'watchlist/addItem',
  async ({ userId, listType, item }) => {
    let saved = await watchOrReadListController.save(userId, listType, item)
    // אם מתקבל מבנה Axios ({ data: ... })
    if (saved && saved.data) saved = saved.data
    // fallback ל־item אם עדיין undefined/null
    return { userId, listType, item: saved ?? item }
  }
)

export const updateItem = createAsyncThunk(
  'watchlist/updateItem',
  async ({ userId, listType, anizoneId, updates }) => {
    const payload = { userId, anizone_id: anizoneId, ...updates }
    let saved = await watchOrReadListController.save(userId, listType, payload)
    if (saved && saved.data) saved = saved.data
    return { userId, listType, item: saved ?? payload }
  }
)

export const removeItem = createAsyncThunk(
  'watchlist/removeItem',
  async ({ userId, listType, anizoneId }) => {
    await watchOrReadListController.remove(userId, listType, anizoneId)
    return { userId, listType, anizoneId }
  }
)

/* Slice */

const watchlistSlice = createSlice({
  name: 'watchlist',
  initialState,
  reducers: {
    clearList(state, { payload: { userId, listType } }) {
      delete state[listType]?.[userId]
      delete state.statusMap[userId]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadList.fulfilled, (state, { payload }) => {
        const { userId, listType, cacheKey, page, list } = payload
        state[listType][userId] ??= {}
        state[listType][userId][cacheKey] ??= {}
        state[listType][userId][cacheKey][page] = list
      })
      .addCase(loadStatusMap.fulfilled, (state, { payload }) => {
        const { userId, map } = payload
        state.statusMap[userId] = map
      })
      .addCase(addItem.fulfilled, upsertItem)
      .addCase(updateItem.fulfilled, upsertItem)
      .addCase(removeItem.fulfilled, (state, { payload }) => {
        const { userId, listType, anizoneId } = payload
        const buckets = state[listType][userId] || {}
        Object.values(buckets).forEach((pages) =>
          Object.keys(pages).forEach(
            (p) =>
              (pages[p] = pages[p].filter((i) => i.anizone_id !== anizoneId))
          )
        )
        delete state.statusMap[userId]?.[anizoneId]
      })
  },
})

/* helper: upsert + update statusMap */

function upsertItem(state, { payload }) {
  if (!payload) return

  let { userId, listType, item } = payload

  /* ── graceful fallback: אם item לא קיים אבל יש anizone_id בשורש ── */
  if (!item && typeof payload.anizone_id !== 'undefined') {
    item = payload // treat whole payload as item
    listType = listType || 'watchlist' // fallback למסלול הנפוץ
  }

  if (!item) {
    // עדיין אין? אין מה לעשות
    console.warn('⚠️ upsertItem: missing item', payload)
    return
  }

  const id = item.anizone_id ?? item.mal_id
  if (id == null) {
    console.warn('⚠️ upsertItem: missing anizone_id', item)
    return
  }

  /* ── ההמשך כמו שהיה ── */
  state[listType][userId] ??= {}
  const buckets = state[listType][userId]

  Object.values(buckets).forEach((pages) =>
    Object.keys(pages).forEach(
      (p) => (pages[p] = upsertArray(pages[p], item, id))
    )
  )

  state.statusMap[userId] ??= {}
  state.statusMap[userId][id] = {
    status: item.status,
    progress: item.progress,
  }
}

function upsertArray(arr = [], item, id) {
  const idx = arr.findIndex((i) => i.anizone_id === id)
  if (idx === -1) arr.push(item)
  else arr[idx] = item
  return arr
}

export const { clearList } = watchlistSlice.actions
export const watchlistReducer = watchlistSlice.reducer
